import { useEffect, useState } from 'react';
import OneSignal from 'react-onesignal';
import { getCookie, setCookie } from '../../utils/cookies-util';
import { sendSubscriptionId } from '../../api/one-signal-api';
import toast from 'react-hot-toast';
import { getErrorString } from '../../utils';
import { PLATFORMS } from '../../constants/generic-constants';
import { getBaseRedirectUrl, getTenantSepratedValues } from 'utils/tenant-util';
import { useSelector } from 'react-redux';
import { selectCurrentTenant } from 'reducers/selectors';
import { BASE_PATH } from 'constants/admin-paths';
import { useIntegrations } from 'hooks';
import { INTEGRATION_MODELS } from 'constants/integrations';

const useOneSignal = () => {
  const validToken = getCookie('access-token') || getCookie('access-token-user');
  const one_signal_id = getCookie('one_signal_id');
  const [active, setActive] = useState(false);
  const { tenantData } = useSelector(selectCurrentTenant);
  const { getPermittedIntegration, integrations } = useIntegrations();

  const getOneSignalAppId = () => {
    const adminOneSignalAppIds = getTenantSepratedValues(process.env.REACT_APP_ADMIN_ONESIGNAL_ID);
    const patientOneSignalAppIds = getTenantSepratedValues(process.env.REACT_APP_PATIENT_ONESIGNAL_ID);

    if (getBaseRedirectUrl({ tenantData }) === BASE_PATH) {
      return adminOneSignalAppIds?.[tenantData?.slug] ?? '';
    } else {
      return patientOneSignalAppIds?.[tenantData?.slug] ?? '';
    }
  };

  const getSafariWebId = () => {
    const adminSafariWebIds = getTenantSepratedValues(process.env.REACT_APP_ADMIN_SAFARI_WEB_ID);
    const patientSafariWebIds = getTenantSepratedValues(process.env.REACT_APP_PATIENT_SAFARI_WEB_ID);

    if (getBaseRedirectUrl({ tenantData }) === BASE_PATH) {
      return adminSafariWebIds?.[tenantData?.slug] ?? '';
    } else {
      return patientSafariWebIds?.[tenantData?.slug] ?? '';
    }
  };

  const appId = getOneSignalAppId();
  const safari_web_id = getSafariWebId();
  const platform = getBaseRedirectUrl({ tenantData }) === BASE_PATH ? PLATFORMS.ADMIN_WEB : PLATFORMS.PATIENT_WEB;
  const subscriptionId = OneSignal.User.PushSubscription?.id;

  useEffect(() => {
    const timer = setTimeout(() => {
      setActive(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!validToken || one_signal_id) return;
    const initialize = async () => {
      try {
        await OneSignal.init({
          appId,
          safari_web_id,
          notifyButton: { enable: true },
          allowLocalhostAsSecureOrigin: true,
          serviceWorkerPath: '/OneSignalSDKWorker.js',
        });
        OneSignal.Slidedown.promptPush();
      } catch (error) {
        console.error('OneSignal initialization failed:', error);
      }
    };

    if (integrations && getPermittedIntegration({ module: INTEGRATION_MODELS.ONESIGNAL })) {
      initialize();
    }
  }, [validToken, one_signal_id, integrations]);

  useEffect(() => {
    const handleSubscribe = async () => {
      if (subscriptionId && validToken && active) {
        try {
          const response = await sendSubscriptionId({ subscription_id: subscriptionId, platform });
          if (response?.status) {
            setCookie('one_signal_id', response?.one_signal_id);
          } else {
            toast.error(getErrorString(response));
          }
        } catch (error) {
          toast.error(getErrorString(error));
        }
      }
    };

    if (getPermittedIntegration({ module: INTEGRATION_MODELS.ONESIGNAL })) {
      handleSubscribe();
    }
  }, [subscriptionId, validToken, active]);

  return { one_signal_id };
};
export default useOneSignal;
